import ChevronLeft from '@mui/icons-material/ChevronLeft';
import { Box, Button, IconButton, Stack, Toolbar } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import { styled } from '@mui/material/styles';
import OpenHelpCentreLink from 'components/common/OpenHelpCentreLink';
import { useAnalytics } from 'providers/AnalyticsProvider';
import React, { useEffect, useMemo } from 'react';
import { getDynamicBranding, styles, theme } from 'theme/theme';

import { appConfig } from '../../config/runtimeConfig';
import { useRouter } from '../../hooks/useRouter';
import { useAppDispatch, useAuth } from '../../redux/hooks';
import authSlice from '../../redux/reducers/authSlice';
import { INVISIBLE_WHILE_SESSION_RESTORING } from '../../utils/sessionCookie';

import CurrencySelector from './CurrencySelector';

interface OwnProps {
  activeRoute?: string;
  showSimple?: boolean;
  title?: string;
  maxwidth: any;
  showCurrencyConvertor?: boolean;
  showBackBtn?: boolean;
  goBack?: () => void;
}

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: 'white',
  borderBottom: '0.75px solid',
  boxShadow: 'none',
  borderBottomColor: '#DDD',
  boxSizing: 'border-box',
  zIndex: theme.zIndex.drawer + 1,
}));

const HeaderNav = (props: OwnProps) => {
  const { user, sessionRestoring } = useAuth();
  // A session cookie means a signed-in visitor is on the way in, so show them
  // the nav they had rather than flashing the signed-out one on every refresh.
  const showSignedInNav = !!user || sessionRestoring;
  const dispatch = useAppDispatch();
  const router = useRouter();
  const boatOwner = useMemo(
    () =>
      user?.roles?.includes('owner') || user?.roles?.includes('request-owner'),
    [user?.roles]
  );
  const analytics = useAnalytics();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const openLoginModalHandler = (showCreate: boolean) => {
    if (typeof window !== 'undefined') {
      const eventName = `Click ${showCreate ? 'sign up' : 'login'}.`;
      analytics.trackEvent(eventName);
    }
    dispatch(authSlice.actions.setAuthState(showCreate ? 'create' : 'login'));
    dispatch(authSlice.actions.openLoginModal());
  };

  const openAddBoatClick = (e: any) => {
    if (typeof window !== 'undefined') {
      const eventName = `Click ${e ? '/manage/vessels' : '/register-to-list'}.`;
      analytics.trackEvent(eventName);
    }
    e.preventDefault();
    if (boatOwner) router.push('/manage/vessels');
    else router.push('/register-to-list');
  };

  const openProfileHandler = (e: any) => {
    if (typeof window !== 'undefined') {
      const eventName = 'Click profile';
      analytics.trackEvent(eventName);
    }
    e.preventDefault();

    router.push('/user-profile');
  };

  const openMessagesHandler = (e: any) => {
    if (typeof window !== 'undefined') {
      const eventName = 'Click header messages';
      analytics.trackEvent(eventName);
    }
    e.preventDefault();
    router.push('/messages');
  };

  const goBack = () => {
    if (typeof window !== 'undefined') {
      const eventName = 'Click header go back';
      analytics.trackEvent(eventName);
    }
    if (props.goBack) {
      props.goBack();
    }
  };

  const {
    brand,
    logo,
    logoWidth,
    logoHeight,
    mobileLogo,
    mobileLogoWidth,
    mobileLogoHeight,
  } = getDynamicBranding();

  // Both layouts are always rendered and CSS picks one (xs = mobile, sm+ =
  // desktop), rather than branching on a JS media query. The homepage is
  // server-rendered, and the server can't know the viewport: a JS branch
  // always sends the desktop header, which phones then paint before
  // hydration swaps it for the mobile one - a visible flash and layout
  // shift, worst on slow connections. Media queries apply before first
  // paint. Logos are lazy so the hidden layout's logo is never fetched.
  return (
    <React.Fragment>
      <Box
        sx={{
          display: { xs: 'block', sm: 'none' },
          backgroundColor: 'white',
          position: 'fixed',
          zIndex: theme.zIndex.drawer + 1,
          top: 0,
          left: 0,
          right: 0,
          height: 'env(safe-area-inset-top, 20px)',
        }}
      />
      <StyledAppBar
        position="fixed"
        data-cy="header-bar"
        sx={{ marginTop: { xs: 'env(safe-area-inset-top, 20px)', sm: 0 } }}
      >
        <Toolbar
          sx={{
            width: '100%',
            maxWidth: props.maxwidth,
            mx: 'auto',
          }}
        >
          <Box
            sx={{
              display: { xs: 'flex', sm: 'none' },
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
              }}
            >
              {props.showBackBtn && (
                <IconButton data-cy="go-back-button" onClick={goBack}>
                  <ChevronLeft sx={{ fontSize: 26, color: 'black' }} />
                </IconButton>
              )}
            </Box>
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <img
                width={mobileLogoWidth}
                height={mobileLogoHeight}
                loading="lazy"
                style={{
                  height:
                    styles.mobileAppBarHeight -
                    (appConfig.APP_BRAND === 'ellipsis' ? 20 : 0),
                  width: 'auto',
                  cursor: 'pointer',
                }}
                src={mobileLogo}
                alt={`${brand} Logo`}
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  router.push(`/`);
                }}
              />
            </Box>
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
              }}
            >
              {props.showCurrencyConvertor && <CurrencySelector />}
            </Box>
          </Box>
          <Box
            sx={{
              display: { xs: 'none', sm: 'flex' },
              alignItems: 'center',
              width: '100%',
            }}
          >
            <img
              width={logoWidth}
              height={logoHeight}
              loading="lazy"
              style={{
                height: styles.appBarHeight - 20,
                width: 'auto',
                padding: '1px 0',
                cursor: 'pointer',
              }}
              src={logo}
              alt={`${brand} Logo`}
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                router.push(`/`);
              }}
            />
            {!props.showSimple && (
              <Stack
                direction="row"
                spacing={1}
                sx={{ width: '100%', justifyContent: 'flex-end' }}
              >
                <CurrencySelector />
                <Button
                  component="a"
                  href={boatOwner ? '/manage/vessels' : '/register-to-list'}
                  variant="text"
                  onClick={openAddBoatClick}
                  data-cy="register-boat-button"
                  // Opt out of Vike's leftover client-router (see
                  // ListingCard.tsx for the full explanation) - this is a
                  // real internal <a href>, so it's equally exposed to the
                  // same two-router navigation race.
                  data-vike="false"
                >
                  {boatOwner ? 'My Listings' : 'Register to List'}
                </Button>
                <Button
                  component="a"
                  href="https://help.ellipsis.sc/hc/1313306050"
                  variant="text"
                  onClick={OpenHelpCentreLink}
                  data-cy="help-centre-button"
                >
                  Help
                </Button>
                {showSignedInNav && (
                  <Button
                    component="a"
                    variant="text"
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      router.push('/bookings');
                    }}
                  >
                    Bookings
                  </Button>
                )}
                {showSignedInNav ? (
                  <React.Fragment>
                    <Button variant="text" onClick={openMessagesHandler}>
                      Messages
                    </Button>

                    <Button
                      variant="text"
                      onClick={openProfileHandler}
                      data-cy={'profile-button'}
                    >
                      Profile
                    </Button>
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    <Button
                      variant="text"
                      onClick={() => openLoginModalHandler(false)}
                      data-cy="login-button"
                      sx={INVISIBLE_WHILE_SESSION_RESTORING}
                    >
                      Login
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => openLoginModalHandler(true)}
                      data-cy="create-account-button"
                      sx={INVISIBLE_WHILE_SESSION_RESTORING}
                    >
                      Sign Up
                    </Button>
                  </React.Fragment>
                )}
              </Stack>
            )}
          </Box>
        </Toolbar>
      </StyledAppBar>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          flexShrink: 0,
          height: {
            xs: styles.mobileAppBarHeight,
            sm: styles.appBarHeight,
          },
        }}
      />
    </React.Fragment>
  );
};

export default HeaderNav;
